import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getAllCompanies } from '../services/companyService';
import Footer from '../components/Footer';

const LandingPage = () => {
    const [stats, setStats] = useState({
        totalCompanies: 0,
        totalJobSeekers: 0,
        totalJobs: 0,
        totalApplications: 0
    });
    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [imageError, setImageError] = useState({});

    const handleImageError = (id) => {
        setImageError((prev) => ({ ...prev, [id]: true }));
    };

    useEffect(() => {
        fetchStats();
        fetchCompanies();

        // Mouse tracking
        const handleMouseMove = (e) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };

        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    const fetchStats = async () => {
        try {
            const jobsSnapshot = await getDocs(collection(db, 'jobs'));
            const applicationsSnapshot = await getDocs(collection(db, 'applications'));
            const companiesSnapshot = await getDocs(collection(db, 'companies'));
            const usersSnapshot = await getDocs(collection(db, 'users'));

            setStats({
                totalCompanies: companiesSnapshot.size,
                totalJobSeekers: usersSnapshot.docs.filter(doc => doc.data().userType === 'jobseeker').length,
                totalJobs: jobsSnapshot.size,
                totalApplications: applicationsSnapshot.size
            });
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const result = await getAllCompanies(10);
            if (result.success) {
                setCompanies(result.data);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };



    const features = [
        {
            icon: (
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
            ),
            title: 'AI-Powered Matching',
            description: 'Advanced algorithms analyze your profile, skills, and preferences to recommend the most relevant opportunities.'
        },
        {
            icon: (
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            ),
            title: 'Instant Applications',
            description: 'Apply to multiple positions with a single click. Your profile and documents are automatically formatted for each application.'
        },
        {
            icon: (
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            ),
            title: 'Real-Time Analytics',
            description: 'Track application status, view counts, and engagement metrics. Get insights into your job search performance.'
        },
        {
            icon: (
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
            ),
            title: 'Smart Notifications',
            description: 'Receive personalized job alerts based on your preferences. Never miss an opportunity that matches your criteria.'
        },
        {
            icon: (
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
            ),
            title: 'Company Insights',
            description: 'Access detailed company profiles, culture reviews, salary ranges, and employee testimonials before applying.'
        },
        {
            icon: (
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
            ),
            title: 'Career Resources',
            description: 'Access expert resume reviews, interview preparation guides, and personalized career coaching sessions.'
        }
    ];

    const howItWorks = [
        {
            step: '01',
            title: 'Create Your Profile',
            description: 'Sign up and build a comprehensive profile showcasing your skills, experience, and career aspirations.',
            icon: '👤'
        },
        {
            step: '02',
            title: 'Discover Opportunities',
            description: 'Browse through thousands of job listings from top companies or let our AI match you with perfect roles.',
            icon: '🔍'
        },
        {
            step: '03',
            title: 'Apply & Get Hired',
            description: 'Submit applications with one click, track your progress, and land your dream job faster than ever.',
            icon: '🎯'
        }
    ];



    return (
        <>
            <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .glass-effect {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }
        
        .hover-lift {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .hover-lift:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }
        
        /* Custom Cursor Styles */
        .custom-cursor {
          position: fixed;
          width: 20px;
          height: 20px;
          border: 2px solid rgba(168, 85, 247, 0.8);
          border-radius: 50%;
          pointer-events: none;
          transform: translate(-50%, -50%);
          transition: all 0.1s ease;
          z-index: 9999;
          mix-blend-mode: difference;
        }
        
        .custom-cursor-trail {
          position: fixed;
          width: 8px;
          height: 8px;
          background: radial-gradient(circle, rgba(168, 85, 247, 0.8) 0%, rgba(236, 72, 153, 0.4) 50%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
          transform: translate(-50%, -50%);
          transition: all 0.15s cubic-bezier(0.75, -0.5, 0.25, 1.5);
          z-index: 9998;
          box-shadow: 0 0 20px rgba(168, 85, 247, 0.6), 0 0 40px rgba(236, 72, 153, 0.4);
        }
        
        @media (max-width: 1024px) {
          .custom-cursor,
          .custom-cursor-trail {
            display: none;
          }
        }
      `}</style>

            {/* Custom Cursor Follower */}
            <div
                className="custom-cursor"
                style={{
                    left: `${mousePosition.x}px`,
                    top: `${mousePosition.y}px`,
                }}
            />
            <div
                className="custom-cursor-trail"
                style={{
                    left: `${mousePosition.x}px`,
                    top: `${mousePosition.y}px`,
                }}
            />

            <div className="bg-white">
                {/* Hero Section */}
                <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 h-screen flex items-start">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }}></div>
                    </div>

                    <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[calc(100vh-4rem)]">
                            <div className="text-white space-y-6 sm:space-y-8 flex flex-col justify-center">
                                <div className="inline-block">
                                    <span className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-full text-sm font-medium backdrop-blur-sm">
                                        🚀 India's Leading Job Portal
                                    </span>
                                </div>

                                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                                    Your Career,
                                    <br />
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                                        Elevated
                                    </span>
                                </h1>

                                <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed max-w-xl">
                                    Connect with India's top employers. Access exclusive opportunities,
                                    personalized recommendations, and career resources designed for your success.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-4">
                                    <Link
                                        to="/register"
                                        className="group relative px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/50"
                                    >
                                        <span className="relative z-10 flex items-center justify-center gap-2">
                                            Get Started Free
                                            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                        </span>
                                        <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                                    </Link>
                                </div>

                                <div className="hidden flex flex-wrap items-center gap-4 sm:gap-6 md:gap-8 pt-6 sm:pt-8 border-t border-white/10">
                                    <div>
                                        <div className="text-2xl sm:text-3xl font-bold">{stats.totalCompanies}+</div>
                                        <div className="text-xs sm:text-sm text-gray-400">Companies</div>
                                    </div>
                                    <div>
                                        <div className="text-2xl sm:text-3xl font-bold">{stats.totalJobSeekers}+</div>
                                        <div className="text-xs sm:text-sm text-gray-400">Job Seekers</div>
                                    </div>
                                    <div>
                                        <div className="text-2xl sm:text-3xl font-bold">{stats.totalApplications}+</div>
                                        <div className="text-xs sm:text-sm text-gray-400">Applications</div>
                                    </div>
                                </div>
                            </div>

                            <div className="hidden lg:flex items-center justify-center">
                                <div className="relative w-full max-w-2xl">
                                    {/* Main image container - Clean and Professional */}
                                    {/* Main image container - Enhanced */}
                                    <div className="relative w-full aspect-[4/3] flex items-center justify-center">
                                        <div className="absolute inset-0 bg-gradient-to-tr from-purple-600/30 to-pink-600/30 rounded-full blur-3xl"></div>
                                        <img
                                            src="/girl_img.png"
                                            alt="Inspired Students"
                                            className="relative z-10 w-full h-auto object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500"
                                            style={{
                                                filter: 'contrast(1.1) saturate(1.1)',
                                                maxHeight: '150%'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>



                {/* How It Works Section */}
                <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12 sm:mb-16">
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                                How It Works
                            </h2>
                            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
                                Get started in three simple steps and land your dream job
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {howItWorks.map((item, index) => (
                                <div
                                    key={index}
                                    className="relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 border-2 border-purple-100 hover:border-purple-300 transition-all duration-300 hover:shadow-xl"
                                >
                                    <div className="absolute top-6 right-6 text-6xl font-bold text-purple-200">
                                        {item.step}
                                    </div>
                                    <div className="text-6xl mb-6">{item.icon}</div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Features Section - Enhanced with Gradient Background */}
                <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }}></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12 sm:mb-16">
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                                Everything You Need to Succeed
                            </h2>
                            <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
                                Professional tools and resources designed to accelerate your career growth
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {features.map((feature, index) => (
                                <div
                                    key={index}
                                    className="group p-8 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-300"
                                >
                                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                                    <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Why Choose Us Section */}
                <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-white via-purple-50 to-pink-50 relative overflow-hidden">
                    <div className="absolute inset-0 opacity-5">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 1px 1px, purple 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }}></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            {/* Left Content */}
                            <div className="space-y-8">
                                <div>
                                    <span className="px-4 py-2 bg-purple-600/10 border border-purple-600/20 rounded-full text-sm font-bold text-purple-600 inline-block mb-4">
                                        Why Choose Us
                                    </span>
                                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                                        Trusted by India's Top
                                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600"> Professionals</span>
                                    </h2>
                                    <p className="text-lg text-gray-600 leading-relaxed">
                                        Join thousand of successful professionals who have transformed their careers with our platform.
                                        We connect talent with opportunity, making job searching seamless and effective.
                                    </p>
                                </div>

                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-600 font-medium">Active Job Listings</div>
                                    </div>

                                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div className="text-sm text-gray-600 font-medium">Partner Companies</div>
                                    </div>

                                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div className="text-3xl font-bold text-gray-900 mb-1">98%</div>
                                        <div className="text-sm text-gray-600 font-medium">Success Rate</div>
                                    </div>

                                    <div className="bg-white rounded-2xl p-6 shadow-lg border border-purple-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-12 h-12 bg-gradient-to-br from-orange-600 to-red-600 rounded-xl flex items-center justify-center">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div className="text-3xl font-bold text-gray-900 mb-1">24/7</div>
                                        <div className="text-sm text-gray-600 font-medium">Support Available</div>
                                    </div>
                                </div>

                                {/* Key Benefits */}
                                <div className="space-y-4">
                                    <div className="flex items-start gap-4">
                                        <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-1">Verified Employers Only</h4>
                                            <p className="text-gray-600 text-sm">All companies are thoroughly vetted to ensure legitimacy and quality opportunities.</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-1">Privacy Protected</h4>
                                            <p className="text-gray-600 text-sm">Your data is encrypted and never shared without your explicit permission.</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900 mb-1">Expert Career Guidance</h4>
                                            <p className="text-gray-600 text-sm">Access to professional career counselors and industry mentors.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Image - Professional Team */}
                            <div className="relative">
                                <div className="absolute -top-6 -right-6 w-72 h-72 bg-gradient-to-br from-purple-400/30 to-pink-400/30 rounded-full blur-3xl"></div>
                                <div className="absolute -bottom-6 -left-6 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full blur-3xl"></div>

                                <div className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl overflow-hidden shadow-2xl">
                                    <img
                                        src="https://bnkgroup.co.in/wp-content/uploads/2024/04/group-indian-businessmen-corporate-people-standing-photo_466689-95815.jpg"
                                        alt="Professional Team"
                                        className="w-full h-auto object-cover"
                                    />

                                    {/* Overlay Stats */}
                                    <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl p-6 shadow-xl">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                {/* <div className="text-2xl font-bold text-gray-900">{stats.totalJobSeekers.toLocaleString()}+</div> */}
                                                <div className="text-sm text-gray-600 font-medium">Thousands Of Professionals Trust Us</div>
                                            </div>
                                            <div className="flex -space-x-2">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 border-2 border-white flex items-center justify-center text-white font-bold text-sm">A</div>
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 border-2 border-white flex items-center justify-center text-white font-bold text-sm">B</div>
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-600 to-emerald-600 border-2 border-white flex items-center justify-center text-white font-bold text-sm">C</div>
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-600 to-red-600 border-2 border-white flex items-center justify-center text-white font-bold text-sm">+</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Success Stories / Testimonials Section */}
                <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }}></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12 sm:mb-16">
                            <span className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-full text-sm font-bold inline-block mb-4">
                                Success Stories
                            </span>
                            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
                                What Our Users Say
                            </h2>
                            <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
                                Real stories from professionals who found their dream careers through our platform
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {/* Testimonial 1 */}
                            <div className="group bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-purple-500/50 transition-all duration-300 hover:-translate-y-2">
                                <div className="flex items-center gap-1 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>
                                <p className="text-gray-300 mb-6 leading-relaxed italic">
                                    "I would like to sincerely thank Udaan Jobs for all the support and guidance. From the first call till the offer closure, the team was very helpful and professional throughout the process.
                                    <br /><br />
                                    Nowadays, many candidates do not receive proper feedback, but with Udaan Jobs, the communication was clear at every step. The entire process was smooth and transparent.
                                    <br /><br />
                                    Udaan Jobs truly helps in bridging the gap between companies and individuals. Wishing the entire team all the very best for the future."
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold text-xl overflow-hidden">
                                        {!imageError['priyanka'] ? (
                                            <img
                                                src="/priyanka.jpg"
                                                alt="Priyanka"
                                                className="w-full h-full object-cover"
                                                onError={() => handleImageError('priyanka')}
                                            />
                                        ) : (
                                            'P'
                                        )}
                                    </div>
                                    <div>
                                        <div className="font-bold text-white">Priyanka Kshirsagar</div>
                                        <div className="text-sm text-gray-400">Successfully Placed Candidate</div>
                                    </div>
                                </div>
                            </div>

                            {/* Testimonial 2 */}
                            <div className="group bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-purple-500/50 transition-all duration-300 hover:-translate-y-2">
                                <div className="flex items-center gap-1 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>
                                <p className="text-gray-300 mb-6 leading-relaxed italic">
                                    "I would like to sincerely thank Ms. Neha Khapare Madam for her excellent support in providing the right and suitable candidate for the Telecaller position through the Udaan App.
                                    <br /><br />
                                    Her understanding of our requirement, quick response, and ability to recommend an appropriate candidate made the hiring process smooth and efficient. The candidate shared was well-aligned with our expectations, which reflects her strong screening and guidance skills."
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="flex -space-x-3">
                                        <div className={`w-14 h-14 relative z-10 rounded-full flex items-center justify-center font-bold text-xl overflow-hidden p-1 border-2 border-slate-800 ${imageError['winnmax'] ? 'bg-gradient-to-br from-blue-600 to-indigo-600 text-white' : 'bg-white text-indigo-600'}`}>
                                            {!imageError['winnmax'] ? (
                                                <img
                                                    src="/winnmax.png"
                                                    alt="WinnMax"
                                                    className="w-full h-full object-contain"
                                                    onError={() => handleImageError('winnmax')}
                                                />
                                            ) : (
                                                'W'
                                            )}
                                        </div>
                                        <div className={`w-14 h-14 relative z-0 rounded-full flex items-center justify-center font-bold text-xl overflow-hidden p-1 border-2 border-slate-800 ${imageError['khare'] ? 'bg-gradient-to-br from-purple-600 to-pink-600 text-white' : 'bg-white text-indigo-600'}`}>
                                            {!imageError['khare'] ? (
                                                <img
                                                    src="/khare.png"
                                                    alt="Khare Fabb Tek"
                                                    className="w-full h-full object-contain"
                                                    onError={() => handleImageError('khare')}
                                                />
                                            ) : (
                                                'K'
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="font-bold text-white">Prashant Khare</div>
                                        <div className="text-sm text-gray-400">Director, WinnMax & Khare Fabb Tek</div>
                                    </div>
                                </div>
                            </div>

                            {/* Testimonial 3 */}
                            <div className="group bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 hover:border-purple-500/50 transition-all duration-300 hover:-translate-y-2">
                                <div className="flex items-center gap-1 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>
                                <p className="text-gray-300 mb-6 leading-relaxed italic">
                                    "I would like to express my sincere appreciation to Udaan Jobs for their excellent support throughout the hiring process. From the initial discussion to the final confirmation, the team was responsive, clear, and supportive at every stage.
                                    <br /><br />
                                    What stood out the most was the timely communication and honest feedback, which made the entire experience smooth and stress-free. Udaan Jobs truly plays an important role in connecting the right talent with the right opportunities. Wishing the team continued success ahead."
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center text-white font-bold text-xl overflow-hidden">
                                        {!imageError['amit'] ? (
                                            <img
                                                src="/amit.jpg"
                                                alt="Amit Ghodake"
                                                className="w-full h-full object-cover"
                                                onError={() => handleImageError('amit')}
                                            />
                                        ) : (
                                            'A'
                                        )}
                                    </div>
                                    <div>
                                        <div className="font-bold text-white">Amit Ghodake</div>
                                        <div className="text-sm text-gray-400">Successfully Placed Candidate</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Trust Badges */}
                        <div className="mt-16 pt-12 border-t border-white/10">
                            <div className="text-center mb-8">
                                <p className="text-gray-400 font-medium">Trusted by professionals from</p>
                            </div>
                            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
                                <div className="text-2xl font-bold text-white/60 hover:text-white transition-colors">TCS</div>
                                <div className="text-2xl font-bold text-white/60 hover:text-white transition-colors">Infosys</div>
                                <div className="text-2xl font-bold text-white/60 hover:text-white transition-colors">Wipro</div>
                                <div className="text-2xl font-bold text-white/60 hover:text-white transition-colors">HCL</div>
                                <div className="text-2xl font-bold text-white/60 hover:text-white transition-colors">Tech Mahindra</div>
                                <div className="text-2xl font-bold text-white/60 hover:text-white transition-colors">Accenture</div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Udaan Story Section */}
                <section className="py-20 sm:py-24 md:py-28 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30 relative overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-[0.03]">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 2px 2px, rgb(59 130 246) 1px, transparent 0)`,
                            backgroundSize: '80px 80px'
                        }}></div>
                    </div>

                    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Header */}
                        <div className="text-center mb-20">
                            <div className="inline-block">
                                <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-6">
                                    From Vision to Reality
                                </h2>
                                <div className="w-32 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto rounded-full"></div>
                            </div>
                            <p className="text-lg text-gray-600 mt-6 max-w-2xl mx-auto">The inspiring journey behind Udaan Jobs</p>
                        </div>

                        {/* Founder Introduction */}
                        <div className="mb-24">
                            <div className="max-w-6xl mx-auto">
                                <div className="bg-white rounded-2xl border border-gray-200 p-8 sm:p-12">
                                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                                        {/* Left side - Founder Image/Info */}
                                        <div className="text-center lg:text-left">
                                            <div className="flex justify-center lg:justify-start mb-6">
                                                <div className="w-32 h-32 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-bold text-4xl overflow-hidden">
                                                    <img
                                                        src="/neha_khapare copy.jpeg"
                                                        alt="Neha Khapare"
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            </div>
                                            <h3 className="text-3xl font-bold text-gray-900 mb-2">Neha Khapare</h3>
                                            <p className="text-blue-600 font-semibold text-lg mb-4">Founder & CEO, Udaan Jobs</p>
                                            <p className="text-gray-600 leading-relaxed">
                                                Building bridges between talent and opportunity, creating a platform where careers flourish without leaving home.
                                            </p>
                                        </div>

                                        {/* Right side - Quote */}
                                        <div>
                                            <blockquote className="text-xl sm:text-2xl text-gray-700 leading-relaxed italic relative">
                                                <span className="text-6xl text-blue-200 absolute -top-4 -left-4 font-serif">"</span>
                                                <div className="pl-8">
                                                    Udaan was born from a realization that talent is everywhere, but opportunity is not. I saw skilled professionals struggling to find meaningful work close to home, and employers facing challenges in connecting with the right talent—right in their own communities.
                                                </div>
                                                <span className="text-6xl text-blue-200 absolute -bottom-8 -right-4 font-serif">"</span>
                                            </blockquote>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Core Values Grid */}
                        <div className="grid md:grid-cols-3 gap-8 mb-24">
                            {/* Vision */}
                            <div className="group">
                                <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-blue-300 transition-all duration-300 text-center h-full">
                                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-105 transition-transform duration-300">
                                        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h4>
                                    <p className="text-gray-600 leading-relaxed">
                                        To create a world where talent and opportunity meet seamlessly—where no one has to leave home to build a fulfilling career.
                                    </p>
                                </div>
                            </div>

                            {/* Mission */}
                            <div className="group">
                                <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-green-300 transition-all duration-300 text-center h-full">
                                    <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-105 transition-transform duration-300">
                                        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h4>
                                    <p className="text-gray-600 leading-relaxed">
                                        To bridge the gap between local talent and meaningful employment through a trusted, transparent, and community-focused platform.
                                    </p>
                                </div>
                            </div>

                            {/* Promise */}
                            <div className="group">
                                <div className="bg-white rounded-2xl p-8 border border-gray-200 hover:border-purple-300 transition-all duration-300 text-center h-full">
                                    <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-105 transition-transform duration-300">
                                        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-2xl font-bold text-gray-900 mb-4">Our Promise</h4>
                                    <p className="text-gray-600 leading-relaxed">
                                        Every job posted is verified, every employer is vetted, and every connection is made with care—because your career deserves nothing less.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Goals Section */}
                        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-12 mb-24 relative overflow-hidden">
                            {/* Background decoration */}
                            <div className="absolute inset-0 opacity-10">
                                <div className="absolute inset-0" style={{
                                    backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                                    backgroundSize: '40px 40px'
                                }}></div>
                            </div>

                            <div className="relative">
                                <div className="text-center mb-12">
                                    <h4 className="text-3xl font-bold text-white mb-4">Our Ambitious Goals</h4>
                                    <div className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-400 mx-auto rounded-full"></div>
                                    <p className="text-gray-300 mt-4">Transforming careers, one connection at a time</p>
                                </div>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                                    <div className="text-center group">
                                        <div className="bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm border border-white border-opacity-20 hover:bg-opacity-20 transition-all duration-300">
                                            <div className="text-4xl font-bold text-blue-400 mb-2 group-hover:scale-110 transition-transform duration-300">10,000+</div>
                                            <p className="text-gray-300 text-sm leading-relaxed">Professionals connected with local opportunities</p>
                                        </div>
                                    </div>
                                    <div className="text-center group">
                                        <div className="bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm border border-white border-opacity-20 hover:bg-opacity-20 transition-all duration-300">
                                            <div className="text-4xl font-bold text-green-400 mb-2 group-hover:scale-110 transition-transform duration-300">1,000+</div>
                                            <p className="text-gray-300 text-sm leading-relaxed">Businesses empowered with homegrown talent</p>
                                        </div>
                                    </div>
                                    <div className="text-center group">
                                        <div className="bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm border border-white border-opacity-20 hover:bg-opacity-20 transition-all duration-300">
                                            <div className="text-4xl font-bold text-purple-400 mb-2 group-hover:scale-110 transition-transform duration-300">1 Year</div>
                                            <p className="text-gray-300 text-sm leading-relaxed">Timeline to achieve our ambitious goals</p>
                                        </div>
                                    </div>
                                    <div className="text-center group">
                                        <div className="bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm border border-white border-opacity-20 hover:bg-opacity-20 transition-all duration-300">
                                            <div className="text-4xl font-bold text-orange-400 mb-2 group-hover:scale-110 transition-transform duration-300">100%</div>
                                            <p className="text-gray-300 text-sm leading-relaxed">Commitment to local talent retention</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Closing Statement */}
                        <div className="text-center">
                            <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-gray-200 p-12">
                                <p className="text-2xl sm:text-3xl text-gray-800 font-medium leading-relaxed mb-8">
                                    "Together, let's create opportunities where they matter most: <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 font-bold">right at home</span>."
                                </p>
                                <div className="border-t border-gray-200 pt-8">
                                    <div className="flex items-center justify-center space-x-4">
                                        <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                                            <img
                                                src="/neha_khapare copy.jpeg"
                                                alt="Neha Khapare"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="text-left">
                                            <p className="text-gray-900 font-semibold text-lg">Neha Khapare</p>
                                            <p className="text-gray-500 text-sm">Building bridges between talent and opportunity</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900 text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }}></div>
                    </div>

                    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                            Ready to Transform Your Career?
                        </h2>
                        <p className="text-base sm:text-lg md:text-xl text-purple-100 mb-8 sm:mb-10 max-w-2xl mx-auto">
                            Join {stats.totalJobSeekers.toLocaleString()}+ professionals who are already using our platform to advance their careers
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/register"
                                className="group px-10 py-4 bg-white text-purple-900 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 shadow-2xl hover:shadow-white/20 inline-flex items-center justify-center gap-2"
                            >
                                Create Your Free Account
                                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                            </Link>
                            <Link
                                to="/login"
                                className="px-10 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-purple-900 transition-all duration-300"
                            >
                                Sign In
                            </Link>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </>
    );
};

export default LandingPage;
